import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { getApiBaseUrl } from '../api/config';
import { resolveShopTheme, themeCssVars, type ShopBranding } from '../theme';
import { useI18n } from '../i18n';
import type { ShopLegal } from '../legal';
import { ShopFooter, ShopHeader, SmallCaps } from '../components/ShopChrome';

/**
 * The venue's terms and its privacy statement, on the venue's own shop.
 *
 * Both documents are written once in `shared/legal` and filled in with the venue's
 * business details, so a venue that has never published a website still has the two
 * pages a payment provider asks for before it may sell gift cards. A venue that does
 * publish its own is linked straight there instead and never reaches this page.
 */

type DocumentKind = 'terms' | 'privacy';

interface LegalSection {
  id: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
}

/** The two documents differ only in what they call the block of contact details. */
interface LegalDocumentResponse {
  title: string;
  intro: string[];
  sections: LegalSection[];
  contact?: { heading: string; intro: string; lines: string[] };
  controller?: { heading: string; intro: string; lines: string[] };
}

interface ShopVenue {
  location_name?: string;
  location_address?: string;
  location_language?: string;
  branding?: ShopBranding;
  legal?: ShopLegal;
}

const isEmail = (line: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(line.trim());

const LegalPage: React.FC<{ kind: DocumentKind }> = ({ kind }) => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { t, locale, applyVenueLocale } = useI18n();

  const [venue, setVenue] = useState<ShopVenue | null>(null);
  const [document_, setDocument] = useState<LegalDocumentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The venue's branding and its legal details paint this page like any other shop page.
  useEffect(() => {
    if (!locationId) return;
    let active = true;
    (async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/gift-vouchers/shop/${locationId}`);
        if (!response.ok || !active) return;
        const data = (await response.json()) as ShopVenue;
        setVenue(data);
        applyVenueLocale(data.location_language);
      } catch {
        /* the document reads fine on plain paper */
      }
    })();
    return () => {
      active = false;
    };
  }, [locationId, applyVenueLocale]);

  // The document itself follows whichever language the shop is being read in.
  useEffect(() => {
    if (!locationId) {
      setError(t('legal.error'));
      setLoading(false);
      return;
    }

    let active = true;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/widget/locations/${encodeURIComponent(locationId)}/${kind}?lang=${encodeURIComponent(locale)}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error(`Failed to load ${kind} (${response.status})`);
        const payload = (await response.json()) as LegalDocumentResponse;
        if (!active) return;
        setDocument(payload);
      } catch (err) {
        if (!active || (err instanceof DOMException && err.name === 'AbortError')) return;
        console.error(`Error fetching ${kind} document:`, err);
        setDocument(null);
        setError(t('legal.error'));
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [locationId, kind, locale, t]);

  useEffect(() => {
    if (document_?.title && typeof window !== 'undefined') {
      window.document.title = document_.title;
    }
  }, [document_?.title]);

  const theme = useMemo(() => resolveShopTheme(venue?.branding), [venue?.branding]);
  const contact = document_?.controller || document_?.contact;

  return (
    <div className="flex min-h-screen flex-col" style={themeCssVars(theme)}>
      <ShopHeader
        theme={theme}
        venueName={venue?.location_name}
        onHome={() => navigate(`/shop/${locationId}`)}
        onCheckVoucher={() => navigate('/voucher')}
      />

      <main className="mx-auto w-full max-w-[760px] flex-1 px-5 py-12 sm:px-10 sm:py-16">
        <button
          type="button"
          onClick={() => navigate(`/shop/${locationId}`)}
          className="mb-8 inline-flex items-center gap-2"
          style={{ font: `500 14px/1 ${theme.fontFamily}`, color: theme.body }}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('legal.back_to_shop')}
        </button>

        {loading && (
          <div className="flex items-center gap-3" style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: theme.accent }} />
            {t('legal.loading')}
          </div>
        )}

        {!loading && error && (
          <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: 0 }}>{error}</p>
        )}

        {!loading && !error && document_ && (
          <article>
            <SmallCaps theme={theme}>
              {kind === 'terms' ? t('legal.terms') : t('legal.privacy')}
            </SmallCaps>
            <h1
              className="mt-4 text-balance"
              style={{
                margin: 0,
                font: `500 clamp(28px, 5vw, 40px)/1.15 ${theme.fontFamily}`,
                letterSpacing: '-.02em',
                color: theme.ink,
              }}
            >
              {document_.title}
            </h1>

            <div className="mt-6 flex flex-col gap-4">
              {document_.intro.map((paragraph, index) => (
                <p key={index} style={{ margin: 0, font: `400 16px/1.7 ${theme.fontFamily}`, color: theme.body }}>
                  {paragraph}
                </p>
              ))}
            </div>

            {document_.sections.map((section) => (
              <section key={section.id} className="mt-10">
                <h2
                  className="pb-3"
                  style={{
                    margin: 0,
                    font: `500 20px/1.3 ${theme.fontFamily}`,
                    color: theme.ink,
                    borderBottom: `1px solid ${theme.hairline}`,
                  }}
                >
                  {section.title}
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {section.paragraphs.map((paragraph, index) => (
                    <p key={index} style={{ margin: 0, font: `400 15px/1.7 ${theme.fontFamily}`, color: theme.body }}>
                      {paragraph}
                    </p>
                  ))}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="flex list-disc flex-col gap-2 pl-5" style={{ margin: 0 }}>
                      {section.bullets.map((bullet, index) => (
                        <li key={index} style={{ font: `400 15px/1.7 ${theme.fontFamily}`, color: theme.body }}>
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            ))}

            {contact && (
              <section className="mt-12 p-6" style={{ background: theme.surface, borderRadius: theme.radius }}>
                <h2 style={{ margin: 0, font: `500 18px/1.3 ${theme.fontFamily}`, color: theme.ink }}>
                  {contact.heading}
                </h2>
                <p className="mt-2" style={{ margin: 0, font: `400 15px/1.7 ${theme.fontFamily}`, color: theme.body }}>
                  {contact.intro}
                </p>
                <address className="mt-4 not-italic">
                  {contact.lines.map((line) => (
                    <div key={line} style={{ font: `400 15px/1.7 ${theme.fontFamily}`, color: theme.ink }}>
                      {isEmail(line) ? (
                        <a href={`mailto:${line}`} className="underline underline-offset-2" style={{ color: theme.ink }}>
                          {line}
                        </a>
                      ) : (
                        line
                      )}
                    </div>
                  ))}
                </address>
              </section>
            )}
          </article>
        )}
      </main>

      <ShopFooter
        theme={theme}
        venueName={venue?.location_name}
        venueAddress={venue?.location_address}
        legal={venue?.legal}
        locationId={locationId}
      />
    </div>
  );
};

export default LegalPage;
